import React from 'react';
import { ResumeData } from './resumeTypes';
import { formatTextForDisplay, escapeHtml } from './textFormatter';
import {
  AppliedTemplate,
  formatDate,
  formatBirthDate,
  getLocalizedText,
} from './resumeUtils';

interface Template02RendererProps {
  data: ResumeData;
  template: AppliedTemplate;
  accentColor: string;
  accentTextColor: string;
}

const Template02Renderer: React.FC<Template02RendererProps> = ({
  data,
  template,
  accentColor,
}) => {
  const t = template;
  const txt = (key: string) => getLocalizedText(key, data.lang, t.translations);
  const textColor = t.colors.text || '#303030';
  const mutedColor = t.colors.muted || '#9CA3AF';
  const baseFontSize = `${t.fonts.scale.base}px`;

  const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="t02-section-header">
      <h2 className="t02-section-title">{children}</h2>
      <div className="t02-section-line" />
    </div>
  );

  const renderHeader = () => (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div style={{ flex: 1 }}>
          <h1 style={{
            fontSize: `${t.fonts.scale.h1}px`,
            fontWeight: 700,
            textTransform: 'uppercase',
            color: textColor,
            margin: 0,
            lineHeight: 1.1,
          }}>
            {data.name?.last || ''} {data.name?.first || ''}
          </h1>
          {data.position && (
            <p style={{ fontSize: `${t.fonts.scale.h3}px`, color: textColor, margin: '6px 0 0', fontWeight: 400 }}>
              {data.position}
            </p>
          )}
        </div>
        {(data.photoUrl || data.photo) && (
          <div
            className="t02-photo-clip"
            style={{
              width: 120, height: 120,
              overflow: 'hidden', borderRadius: 12, flexShrink: 0,
            }}
          >
            <img
              src={data.photo || data.photoUrl}
              alt="Фото"
              crossOrigin="anonymous"
              className="resume-cover-photo"
              style={{
                width: '100%', height: '100%',
                objectFit: 'cover', objectPosition: 'center',
                display: 'block',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );

  const renderContacts = () => {
    if (!data.contacts?.email && (!data.contacts?.phones || data.contacts.phones.length === 0)) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <SectionTitle>{txt('contacts')}</SectionTitle>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 32px', fontSize: baseFontSize, marginTop: 8 }}>
          {data.contacts?.phones?.map((phone, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14 }}>📞</span>
              <span style={{ color: textColor }}>{phone}</span>
            </div>
          ))}
          {data.contacts?.email && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14 }}>📧</span>
              <span style={{ color: textColor }}>{data.contacts.email}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSummary = () => {
    if (!data.about || data.about.trim() === '') return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <SectionTitle>{txt('summary')}</SectionTitle>
        <div
          className="t02-body-text"
          style={{ fontSize: baseFontSize, marginTop: 8 }}
          dangerouslySetInnerHTML={{ __html: escapeHtml(data.about).replace(/\\n/g, '\n').replace(/\n/g, '<br>') }}
        />
      </div>
    );
  };

  const renderExperience = () => {
    if (!data.experience || data.experience.length === 0) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <SectionTitle>{txt('experience')}</SectionTitle>
        <div style={{ marginTop: 8 }}>
          {data.experience.map((exp, i) => (
            <div key={i} className="avoid-break" style={{ marginBottom: i < data.experience!.length - 1 ? 12 : 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <h3 style={{ fontSize: `${t.fonts.scale.h3}px`, fontWeight: 600, color: textColor, margin: 0 }}>
                  {exp.company}
                </h3>
                <span style={{ fontSize: baseFontSize, color: mutedColor, whiteSpace: 'nowrap', marginLeft: 8 }}>
                  {exp.dateFrom ? formatDate(exp.dateFrom) : '—'} – {exp.dateTo ? formatDate(exp.dateTo) : 'н.в.'}
                </span>
              </div>
              <div style={{ fontStyle: 'italic', fontSize: baseFontSize, color: textColor, margin: '2px 0 4px' }}>
                {exp.title}
              </div>
              {exp.description && (
                <div
                  className="t02-body-text"
                  style={{ fontSize: baseFontSize }}
                  dangerouslySetInnerHTML={{ __html: formatTextForDisplay(exp.description) }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderEducation = () => {
    if (!data.education || data.education.length === 0) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <SectionTitle>{txt('education')}</SectionTitle>
        <div style={{ marginTop: 8 }}>
          {data.education.map((edu, i) => (
            <div key={i} className="avoid-break" style={{ marginBottom: i < data.education!.length - 1 ? 10 : 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <h3 style={{ fontSize: `${t.fonts.scale.h3}px`, fontWeight: 600, color: textColor, margin: 0 }}>
                  {edu.org}{edu.specialty ? `, ${edu.specialty}` : ''}{edu.faculty ? `, ${edu.faculty}` : ''}
                </h3>
                <span style={{ fontSize: baseFontSize, color: mutedColor, whiteSpace: 'nowrap', marginLeft: 8 }}>
                  {edu.graduationYear}
                </span>
              </div>
              {edu.degree && (
                <div style={{ fontStyle: 'italic', fontSize: baseFontSize, color: textColor, marginTop: 2 }}>
                  {edu.degree}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCourses = () => {
    if (!data.courses || data.courses.length === 0) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <SectionTitle>{txt('courses')}</SectionTitle>
        <div style={{ marginTop: 8 }}>
          {data.courses.map((course, i) => (
            <div key={i} className="avoid-break" style={{ marginBottom: i < data.courses!.length - 1 ? 10 : 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <h3 style={{ fontSize: `${t.fonts.scale.h3}px`, fontWeight: 600, color: textColor, margin: 0 }}>
                  {course.name}
                </h3>
                {course.year && (
                  <span style={{ fontSize: baseFontSize, color: mutedColor, whiteSpace: 'nowrap', marginLeft: 8 }}>
                    {course.year}
                  </span>
                )}
              </div>
              {course.description && (
                <div className="t02-body-text" style={{ fontSize: baseFontSize, marginTop: 4 }}>
                  {course.description}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSkills = () => {
    if (!data.skills || data.skills.length === 0) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <SectionTitle>{txt('skills')}</SectionTitle>
        <div className="t02-skills-grid" style={{ marginTop: 8 }}>
          {data.skills.map((skill, i) => (
            <div key={i} className="t02-skill-card">
              <div className="t02-skill-label">
                <span style={{ fontSize: baseFontSize }}>{skill}</span>
              </div>
              <div className="t02-skill-bar">
                <span className="t02-bar-seg" /><span className="t02-bar-seg" />
                <span className="t02-bar-seg" /><span className="t02-bar-seg" />
                <span className="t02-bar-seg" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderLanguages = () => {
    if (!data.languages || data.languages.length === 0) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <SectionTitle>{txt('languages')}</SectionTitle>
        <div style={{ marginTop: 8, fontSize: baseFontSize }}>
          {data.languages.map((lang, i) => (
            <div key={i} style={{ marginBottom: 6 }}>
              <div style={{ fontWeight: 500, color: textColor }}>{lang.name}</div>
              <div style={{ color: mutedColor }}>{lang.level}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderHobbies = () => {
    if (!data.hobbies || data.hobbies.length === 0) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <SectionTitle>{txt('hobbies') === 'hobbies' ? 'УВЛЕЧЕНИЯ' : txt('hobbies')}</SectionTitle>
        <div style={{ marginTop: 8, fontSize: baseFontSize }}>
          {data.hobbies.map((hobby, i) => {
            const name = typeof hobby === 'string' ? hobby : hobby.name;
            const desc = typeof hobby === 'string' ? undefined : hobby.description;
            return (
              <div key={i} className="avoid-break" style={{ marginBottom: 8 }}>
                <div style={{ fontWeight: 600, color: textColor }}>{name}</div>
                {desc && <div className="t02-body-text" style={{ color: textColor }}>{desc}</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderMeta = () => {
    const items: Array<{ label: string; value: string }> = [];
    if (data.location?.city) items.push({ label: 'Город проживания', value: data.location.city });
    if (data.birthDate) items.push({ label: 'Дата рождения', value: formatBirthDate(data.birthDate) });
    if (data.meta?.citizenship) items.push({ label: 'Гражданство', value: data.meta.citizenship });
    if (data.meta?.relocation) items.push({ label: 'Готов к переезду', value: 'Да' });
    if (data.meta?.travelReady) items.push({ label: 'Готов к командировкам', value: 'Да' });
    if (data.meta?.employment) items.push({ label: 'Занятость', value: data.meta.employment });
    if (data.meta?.schedule) items.push({ label: 'График работы', value: data.meta.schedule });
    if (items.length === 0) return null;

    return (
      <div style={{ marginBottom: 16 }}>
        <SectionTitle>{txt('meta') === 'meta' ? 'ДОПОЛНИТЕЛЬНАЯ ИНФОРМАЦИЯ' : txt('meta')}</SectionTitle>
        <div className="t02-meta-grid" style={{ marginTop: 8, fontSize: baseFontSize }}>
          {items.map((item, i) => (
            <div key={i} style={{ marginBottom: 6 }}>
              <div style={{ color: mutedColor }}>{item.label}</div>
              <div style={{ fontWeight: 600, color: textColor }}>{item.value}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const css = `
    /* ── Section header: bold text + thin line underneath ── */
    .template-template-02 .t02-section-header {
      margin-bottom: 4px;
    }
    .template-template-02 .t02-section-title {
      font-size: 18px;
      font-weight: 700;
      color: ${textColor};
      text-transform: uppercase;
      margin: 0;
      padding: 0;
      line-height: 1.3;
    }
    .template-template-02 .t02-section-line {
      width: 100%;
      height: 1px;
      background: ${t.colors.border || '#D1D5DB'};
      margin-top: 6px;
    }

    /* ── Body text ── */
    .template-template-02 .t02-body-text {
      color: ${textColor};
      line-height: 1.5;
    }
    .template-template-02 .t02-body-text ul {
      list-style-type: disc;
      padding-left: 16px;
      margin: 4px 0 0;
    }
    .template-template-02 .t02-body-text ul li {
      margin: 0;
      padding: 0;
    }

    /* ── Photo clip container ── */
    .template-template-02 .t02-photo-clip {
      width: 120px;
      height: 120px;
      overflow: hidden;
      border-radius: 12px;
      flex-shrink: 0;
    }

    /* ── Skills: 2-col grid, colored dot + name, dash-bar below ── */
    .template-template-02 .t02-skills-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px 24px;
    }
    .template-template-02 .t02-skill-card {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .template-template-02 .t02-skill-label {
      display: flex;
      align-items: center;
      gap: 6px;
      color: ${textColor};
    }
    .template-template-02 .t02-skill-dot {
      width: 8px;
      height: 8px;
      min-width: 8px;
      border-radius: 50%;
      background: ${accentColor};
      flex-shrink: 0;
    }
    .template-template-02 .t02-skill-bar {
      display: flex;
      gap: 6px;
      padding-left: 14px;
    }
    .template-template-02 .t02-bar-seg {
      width: 24px;
      height: 2px;
      background: ${t.colors.border || '#D1D5DB'};
      border-radius: 1px;
    }

    /* ── Meta: 2-col grid, label above + bold value below ── */
    .template-template-02 .t02-meta-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 4px 24px;
    }

    /* ── Utility ── */
    .template-template-02 .avoid-break {
      break-inside: avoid;
      page-break-inside: avoid;
    }
  `;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <div
        className="single-content template-02-with-stripe"
        style={{ display: 'flex', width: '100%', minHeight: '100%' }}
      >
        <div style={{ width: 40, minWidth: 40, flexShrink: 0, background: accentColor }} aria-hidden />
        <div style={{ flex: 1, padding: '12mm 80px 12mm 40px', boxSizing: 'border-box' }}>
          {renderHeader()}
          {renderContacts()}
          {renderSummary()}
          {renderExperience()}
          {renderEducation()}
          {renderCourses()}
          {renderSkills()}
          {renderLanguages()}
          {renderHobbies()}
          {renderMeta()}
        </div>
      </div>
    </>
  );
};

export default Template02Renderer;
